import EcsClient from '@skype/ecsclient';
import {
  ConfigModel,
  EcsCachedConfig,
  EcsConfigType
} from '@skype/ecsclient/dist/Models';
import * as SyncTasks from 'synctasks';
import { IContext } from '../teams-sdk/teamsSdk';
import { EcsConfig } from './EcsConfig';
import { TeamsRing } from './TeamsRing';

export const ecsClient: EcsClient<EcsConfig> = new EcsClient<EcsConfig>();

let ecsConfig: EcsConfig | undefined;

if (process.env.REACT_APP_CLIENT_SDK_MODE === 'fake-client-sdk') {
  ecsConfig = new EcsConfig();
  ecsConfig.TeamsCustomerEngagementHub = {
    EnableCalling: false,
    EnableEmail: true,
    SetupAutoReply: true,
    WatermarkPollingTime: 10000,
    ExistingGroups: false,
    ExistingSharedMailboxes: false
  };
}

export const initializeEcsClient = async (context: IContext): Promise<void> => {
  let teamsRing = context.ringId;
  let tenantId = context.tid;

  // ECS only supports rings [ring0, ring0_s, ring1, ring1_5, ring1_6, ring2, ring3, ring3_9, general]
  // Therefore, we need to translate other rings to these predefined rings
  // tslint:disable-next-line: max-line-length ~ Disabling for long URL to be on single line
  // Microsoft Teams Rings: https://domoreexp.visualstudio.com/Teamspace/_wiki/wikis/Teamspace.wiki?pagePath=%2FHome%2FDevelopment%2FRings&pageId=242&wikiVersion=GBwikiMaster
  switch (context.ringId) {
    case TeamsRing.ring0:
    case TeamsRing.ring0_s:
    case TeamsRing.ring1:
    case TeamsRing.ring1_5:
    case TeamsRing.ring1_6:
    case TeamsRing.ring2:
    case TeamsRing.ring3:
      teamsRing = context.ringId;
      break;
    case TeamsRing.ring3_6:
      teamsRing = TeamsRing.ring3_9;
      break;
    case TeamsRing.ring3_9:
    case TeamsRing.general:
      teamsRing = context.ringId;
      break;
    case TeamsRing.general_gcc:
      teamsRing = TeamsRing.general;
      break;
    default:
      teamsRing = TeamsRing.general;
      break;
  }

  if (teamsRing && tenantId) {
    let configModel = getEcsClientConfigModel(teamsRing, tenantId);
    await loadEcsConfig(configModel);
  }
};

export const getEcsConfig = async (): Promise<EcsConfig> => {
  if (ecsConfig) {
    return ecsConfig;
  } else {
    return await retrieveAndUpdateEcsConfigFromEcsClient();
  }
};

export const overrideEcsConfig = (
  config: EcsCachedConfig<EcsConfig> | undefined
): void => {
  updateEcsConfig(config);
};

const loadEcsConfig = async (
  config: ConfigModel<EcsConfig>
): Promise<EcsConfig> => {
  await ecsClient.initialize(config);
  updateEcsConfig(ecsClient.getConfig());

  return await retrieveAndUpdateEcsConfigFromEcsClient();
};

const getEcsClientConfigModel = (
  teamsRing: string,
  tenantId: string
): ConfigModel<EcsConfig> => {
  return {
    clientName: 'MicrosoftTeams',
    clientVersion: '1.0.0.0',
    configsToFetch: [EcsConfigType.Default],
    hosts: ['https://config.teams.microsoft.com'],
    initialAppActiveState: true,
    getEcsParameters: (): SyncTasks.Promise<{
      [key: string]: string | number;
    }> =>
      SyncTasks.Resolved({
        agents: 'TeamsCustomerEngagementHub',
        AudienceGroup: teamsRing,
        TenantID: tenantId
      })
  };
};

const retrieveAndUpdateEcsConfigFromEcsClient = (): Promise<EcsConfig> => {
  return new Promise<EcsConfig>((resolve, reject) => {
    let handleEcsClientConfigUpdatedSubscription = () => {
      handleEcsClientConfigUpdated(resolve, reject);
      ecsClient.configUpdated.unsubscribe(
        handleEcsClientConfigUpdatedSubscription
      );
    };

    ecsClient.configUpdated.subscribe(handleEcsClientConfigUpdatedSubscription);
  });
};

const handleEcsClientConfigUpdated = (
  resolve: (value?: EcsConfig | PromiseLike<EcsConfig> | undefined) => void,
  reject: (reason?: string) => void
): void => {
  let ecsConfigUpdated = ecsClient.getConfig();

  if (ecsConfigUpdated) {
    updateEcsConfig(ecsConfigUpdated);
    resolve(ecsConfigUpdated.config);
  } else {
    reject('Error retrieving ECS config');
  }
};

const updateEcsConfig = (
  config: EcsCachedConfig<EcsConfig> | undefined
): void => {
  if (config) {
    ecsConfig = config.config;
  }
};
